import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Stack,
  Grid,
  Tooltip,
  DialogContentText,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  AttachFile as AttachFileIcon,
  Send as SendIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import BrokerCommentDialog from './components/BrokerCommentDialog';

const PolicyDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [underwriterNote, setUnderwriterNote] = useState('');
  const [brokerNote, setBrokerNote] = useState('');
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = React.useRef(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [currentUser] = useState('John Smith'); // 添加当前用户状态，模拟当前登录的用户
  const [openCommentDialog, setOpenCommentDialog] = useState(false);

  // 将模拟数据转换为状态
  const [policyData, setPolicyData] = useState({
    id: id,
    type: 'Home Insurance',
    policyNumber: 'HOME-2024-002',
    client: 'Emma Johnson',
    broker: 'John Smith',
    status: 'requires_info',
    lastUpdated: '2024-01-25',
    requiredDocuments: [
      { name: 'Proof of Ownership', required: true },
      { name: 'Home Inspection Report', required: true },
      { name: 'Previous Insurance History', required: false }
    ],
    existingDocuments: [
      {
        id: 1,
        name: 'Auto Insurance Application.pdf',
        uploadedAt: '2024-01-20',
        status: 'In Review',
        remarks: 'Address verification required',
        required: true,
        previewUrl: '/path/to/document.pdf'
      },
      {
        id: 2,
        name: 'PAC Form.pdf',
        uploadedAt: '2024-01-21',
        status: 'Approved',
        required: true
      },
      {
        id: 3,
        name: 'Void Cheque.pdf',
        uploadedAt: '2024-01-21',
        status: 'Rejected',
        remarks: 'Image quality too low',
        required: true
      },
      {
        id: 4,
        name: 'Winter Tire Invoice.pdf',
        uploadedAt: '2024-01-22',
        status: 'Rejected',
        remarks: 'Document expired',
        required: false
      }
    ],
    underwritingNotes: [
      { note: 'Need clarification on property age', date: '2024-01-22', author: 'Sarah Wilson' },
      { note: 'Requesting additional photos of security system', date: '2024-01-23', author: 'Sarah Wilson' }
    ],
    brokerNotes: [
      { note: 'Client will provide inspection report by next week', date: '2024-01-24', author: 'John Smith' }
    ]
  });

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedFiles([...uploadedFiles, {
        name: file.name,
        size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
        uploadedAt: new Date().toLocaleString(),
        status: 'Pending Review'
      }]);
    }
    setOpenUploadDialog(false);
  };

  const handleDocumentClick = (doc) => {
    // 在新窗口打开文档预览页面
    const previewUrl = `/analysis/document-preview/${doc.id}`;
    const documentState = { document: doc };
    
    // 将 state 数据编码并作为 URL 参数传递
    const stateParam = encodeURIComponent(JSON.stringify(documentState));
    const fullUrl = `${previewUrl}?state=${stateParam}`;
    
    // 使用 window.open 在新窗口打开
    window.open(fullUrl, '_blank');
  };

  const handleDownload = (doc) => {
    // 处理下载文档逻辑
    console.log('Downloading document:', doc);
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(files);
  };

  const handleSubmitResponse = () => {
    setOpenCommentDialog(true);
  };

  const handleConfirmSubmit = () => {
    const currentTime = new Date().toISOString();
    let updatedDocuments = [...policyData.existingDocuments];
    let updatedNotes = [...policyData.brokerNotes];
    
    if (selectedFiles.length > 0) {
      // 创建新的文档记录
      const newDocuments = selectedFiles.map(file => ({
        id: Math.random().toString(36).substr(2, 9),
        name: file.name,
        uploadedAt: new Date().toLocaleDateString(),
        status: 'In Review',
        required: false,
        size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
        remarks: brokerNote.trim() ? `Broker note: ${brokerNote}` : undefined,
        previewUrl: URL.createObjectURL(file) // 为上传的文件创建预览URL
      }));

      // 更新文档列表
      updatedDocuments = [...newDocuments, ...updatedDocuments];
      
      // 添加文件上传记录到笔记
      const fileNote = {
        note: `Uploaded new document${selectedFiles.length > 1 ? 's' : ''}: ${selectedFiles.map(f => f.name).join(', ')}`,
        date: currentTime,
        author: policyData.broker,
        type: 'file_upload',
        documents: newDocuments.map(doc => ({
          id: doc.id,
          name: doc.name
        }))
      };
      updatedNotes = [fileNote, ...updatedNotes];
    }

    if (brokerNote.trim()) {
      const textNote = {
        note: brokerNote,
        date: currentTime,
        author: policyData.broker,
        type: 'text',
        documents: selectedFiles.length > 0 ? selectedFiles.map((file, index) => ({
          id: updatedDocuments[index].id,
          name: file.name
        })) : undefined
      };
      updatedNotes = [textNote, ...updatedNotes];
    }

    // 更新状态
    setPolicyData(prevData => ({
      ...prevData,
      existingDocuments: updatedDocuments,
      brokerNotes: updatedNotes
    }));

    // 清理状态
    setBrokerNote('');
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setOpenCommentDialog(false);
  };

  const renderNoteContent = (note) => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* 笔记内容 */}
        <Typography 
          variant="body2" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            color: note.type === 'file_upload' ? '#1976d2' : 'inherit'
          }}
        >
          {note.type === 'file_upload' && <CloudUploadIcon fontSize="small" />}
          {note.note}
        </Typography>

        {/* 相关文档链接 */}
        {note.documents && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Related documents:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {note.documents.map((doc, index) => (
                <Chip
                  key={index}
                  size="small"
                  label={doc.name}
                  onClick={() => {
                    const fullDoc = policyData.existingDocuments.find(d => d.id === doc.id);
                    if (fullDoc) handleDocumentClick(fullDoc);
                  }}
                  icon={<AttachFileIcon />}
                  sx={{ 
                    bgcolor: 'rgba(25, 118, 210, 0.08)',
                    '&:hover': {
                      bgcolor: 'rgba(25, 118, 210, 0.12)',
                      cursor: 'pointer'
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  // 修改删除文档的处理函数
  const handleDeleteClick = (doc) => {
    setDocumentToDelete(doc);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (documentToDelete) {
      setPolicyData(prev => ({
        ...prev,
        existingDocuments: prev.existingDocuments.filter(doc => doc.id !== documentToDelete.id)
      }));
    }
    setDeleteDialogOpen(false);
    setDocumentToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDocumentToDelete(null);
  };

  // 添加删除留言的处理函数
  const handleDeleteNote = (note) => {
    setPolicyData(prev => ({
      ...prev,
      brokerNotes: prev.brokerNotes.filter(n => n.date !== note.date),
      // 只过滤 brokerNotes，保持 underwritingNotes 不变
    }));
  };

  return (
    <Container maxWidth="xl">
      {/* Hero Section */}
      <Box sx={{ 
        pt: 4,
        pb: 3,
        textAlign: 'center',
        position: 'relative',
        borderBottom: '1px solid #e0e0e0',
        mb: 4
      }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/analysis')}
          startIcon={<ArrowBackIcon />}
          sx={{
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#1976d2',
            borderColor: '#1976d2',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: 'rgba(25, 118, 210, 0.04)'
            }
          }}
        >
          Back to Analysis
        </Button>

        <Typography 
          variant="h4" 
          sx={{ 
            color: '#1976d2',
            fontWeight: 600,
            mb: 1
          }}
        >
          Policy Details
        </Typography>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            color: '#666',
            maxWidth: '600px',
            mx: 'auto'
          }}
        >
          Review detailed policy information and documents
        </Typography>
      </Box>

      {/* Main Content - Two Column Layout */}
      <Grid container spacing={3}>
        {/* Left Column - Documents Section */}
        <Grid item xs={12} md={7}>
          <Paper elevation={2} sx={{ p: 3, height: 'calc(100vh - 180px)', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachFileIcon /> Submitted Documents
              </Typography>
              <Chip 
                label={`${policyData.existingDocuments.length} Documents`}
                size="small"
                sx={{ bgcolor: '#e3f2fd', color: '#1976d2' }}
              />
            </Box>
            
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {policyData.existingDocuments.map((doc) => (
                <Paper
                  key={doc.id}
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: 2,
                    border: '1px solid #e0e0e0',
                    borderLeft: `4px solid ${
                      doc.status === "Approved" ? '#4caf50' :
                      doc.status === "In Review" ? '#ff9800' :
                      '#f44336'
                    }`,
                    bgcolor: '#fff',
                    transition: 'all 0.2s ease',
                    '&:hover': { 
                      bgcolor: '#f8f9fa',
                      borderColor: doc.status === "Approved" ? '#4caf50' :
                                  doc.status === "In Review" ? '#ff9800' :
                                  '#f44336',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {doc.name}
                        </Typography>
                        {doc.required && (
                          <Chip 
                            label="Required" 
                            size="small"
                            sx={{ 
                              height: '20px',
                              bgcolor: '#fff3e0',
                              color: '#ed6c02',
                              '& .MuiChip-label': { px: 1, fontSize: '0.75rem' }
                            }}
                          />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Uploaded on {doc.uploadedAt}
                      </Typography>
                      {doc.remarks && (
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            mt: 1,
                            pl: 2,
                            py: 1,
                            borderLeft: '2px solid #1976d2',
                            bgcolor: 'rgba(25, 118, 210, 0.04)',
                            borderRadius: '0 4px 4px 0'
                          }}
                        >
                          {doc.remarks}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip
                        label={doc.status}
                        size="small"
                        sx={{
                          bgcolor: doc.status === "Approved" ? '#e8f5e9' :
                                  doc.status === "In Review" ? '#fff3e0' :
                                  '#ffebee',
                          color: doc.status === "Approved" ? '#2e7d32' :
                                 doc.status === "In Review" ? '#ed6c02' :
                                 '#d32f2f',
                          fontWeight: 500
                        }}
                      />
                      <Tooltip title="Preview">
                        <IconButton 
                          size="small"
                          onClick={() => handleDocumentClick(doc)}
                          sx={{ color: '#1976d2' }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton 
                          size="small"
                          onClick={() => handleDeleteClick(doc)}
                          sx={{ 
                            color: '#666',
                            '&:hover': {
                              color: '#d32f2f',
                              bgcolor: 'rgba(211, 47, 47, 0.04)'
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Right Column - Discussion Thread */}
        <Grid item xs={12} md={5}>
          <Paper elevation={2} sx={{ p: 3, height: 'calc(100vh - 180px)', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon /> Discussion Thread
              </Typography>
              <Chip 
                label={`${policyData.underwritingNotes.length + policyData.brokerNotes.length} Notes`}
                size="small"
                sx={{ bgcolor: '#e3f2fd', color: '#1976d2' }}
              />
            </Box>

            {/* Notes Timeline - 新样式 */}
            <Box sx={{ flex: 1, overflow: 'auto', mb: 3 }}>
              {[...policyData.underwritingNotes, ...policyData.brokerNotes]
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .map((note, index) => {
                  const isUnderwriter = note.author.includes('Wilson');
                  const noteColor = isUnderwriter ? '#d32f2f' : '#1976d2';
                  const noteBgColor = isUnderwriter ? '#ffebee' : '#e3f2fd';
                  const canDelete = note.author === currentUser; // 检查是否可以删除
                  
                  return (
                    <Box
                      key={index}
                      sx={{
                        mb: 2,
                        p: 2,
                        bgcolor: noteBgColor,
                        borderRadius: 1,
                        border: '1px solid #e0e0e0',
                        borderLeft: `4px solid ${noteColor}`,
                        position: 'relative',
                        '&:hover .delete-button': {
                          opacity: 1
                        }
                      }}
                    >
                      {/* 时间和作者信息 */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={isUnderwriter ? 'Underwriter' : 'Broker'}
                            size="small"
                            sx={{
                              height: '20px',
                              bgcolor: 'white',
                              color: noteColor,
                              border: `1px solid ${noteColor}`,
                              '& .MuiChip-label': { px: 1, fontSize: '0.75rem' }
                            }}
                          />
                          <Typography variant="caption" sx={{ color: noteColor }}>
                            {note.author}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography 
                            variant="caption" 
                            sx={{ color: noteColor }}
                          >
                            {new Date(note.date).toLocaleString()}
                          </Typography>
                          {/* 只对自己的留言显示删除按钮 */}
                          {canDelete && (
                            <IconButton
                              size="small"
                              className="delete-button"
                              onClick={() => handleDeleteNote(note)}
                              sx={{ 
                                opacity: 0,
                                transition: 'opacity 0.2s',
                                p: 0.5,
                                color: '#666',
                                '&:hover': {
                                  color: '#d32f2f',
                                  bgcolor: 'rgba(211, 47, 47, 0.04)'
                                }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Box>

                      {/* 笔记内容 */}
                      {renderNoteContent(note)}
                    </Box>
                  );
                })}
            </Box>

            {/* 笔记输入区域 */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                bgcolor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e0e0e0'
              }}
            >
              <Typography variant="subtitle1" sx={{ color: '#1976d2', fontWeight: 500, mb: 2 }}>
                Add Comment
              </Typography>
              
              {/* 显示选中的文件 */}
              {selectedFiles.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Selected files:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedFiles.map((file, index) => (
                      <Chip
                        key={index}
                        label={file.name}
                        size="small"
                        onDelete={() => {
                          setSelectedFiles(files => files.filter((_, i) => i !== index));
                        }}
                        sx={{ 
                          bgcolor: '#e3f2fd',
                          color: '#1976d2',
                          '& .MuiChip-deleteIcon': {
                            color: '#1976d2',
                            '&:hover': { color: '#d32f2f' }
                          }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Type your comment..."
                value={brokerNote}
                onChange={(e) => setBrokerNote(e.target.value)}
                sx={{ mb: 2 }}
              />
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<AttachFileIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ 
                    flex: 1,
                    borderColor: '#1976d2',
                    color: '#1976d2',
                    '&:hover': {
                      borderColor: '#1565c0',
                      bgcolor: 'rgba(25, 118, 210, 0.04)'
                    }
                  }}
                >
                  Attach Files
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={handleSubmitResponse}
                  disabled={!brokerNote.trim() && selectedFiles.length === 0}
                  sx={{
                    flex: 2,
                    bgcolor: '#1976d2',
                    '&:hover': {
                      bgcolor: '#1565c0'
                    }
                  }}
                >
                  Submit
                </Button>
              </Box>

              {/* 隐藏的文件输入 */}
              <input
                type="file"
                multiple
                ref={fileInputRef}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              />
            </Paper>
          </Paper>
        </Grid>
      </Grid>

      {/* Upload Dialog */}
      <Dialog
        open={openUploadDialog}
        onClose={() => setOpenUploadDialog(false)}
      >
        <DialogTitle>Upload Document</DialogTitle>
        <DialogContent>
          <input
            type="file"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            id="file-upload"
          />
          <label htmlFor="file-upload">
            <Button
              variant="outlined"
              component="span"
              startIcon={<CloudUploadIcon />}
              sx={{ mt: 2 }}
            >
              Choose File
            </Button>
          </label>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUploadDialog(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* 添加删除确认对话框 */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title" sx={{ color: '#d32f2f' }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the document "{documentToDelete?.name}"? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button 
            onClick={handleCancelDelete}
            variant="outlined"
            sx={{ 
              color: '#666',
              borderColor: '#666',
              '&:hover': {
                borderColor: '#666',
                bgcolor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            sx={{
              bgcolor: '#d32f2f',
              '&:hover': {
                bgcolor: '#c62828'
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* 添加 BrokerCommentDialog */}
      <BrokerCommentDialog
        open={openCommentDialog}
        onClose={() => setOpenCommentDialog(false)}
        onSubmit={handleConfirmSubmit}
        comment={brokerNote}
        files={selectedFiles}
      />
    </Container>
  );
};

export default PolicyDetailPage; 